import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { ReactComponent as BackgroundSvg } from 'assets/img/concierge-background.svg';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useQuery } from '@tanstack/react-query';
import { conciergeAPI, PromptsItem } from 'api/concierge';
import { useDispatch, useSelector } from 'react-redux';
import IconHandler from 'components/common/icon/IconHandler';
import { renderShowdown } from 'tools/safeHtml';
import { IDomain, setDefaultSelectedDomains } from 'store/conciergeSlice';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { ICustomStyles } from '../index';
import defaultStyles from './styles';

export default function WelcomePage({
    setActiveChat,
    setActiveChatRequest,
    welcomeIcon,
    title,
    description,
    suggestedPrompts,
    welcomeIconHeight,
    welcomeIconWidth,
}: {
    setActiveChat: (id: number) => void;
    setActiveChatRequest: (request: string) => void;
    welcomeIcon?: string;
    title?: string;
    description?: string;
    suggestedPrompts?: string[];
    welcomeIconWidth?: number;
    welcomeIconHeight?: number;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const [currentInitialDomainsPrompts, setCurrentInitialDomainsPrompts] = useState<string[] | null>(null);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.welcomePage, 'container');
    }, [customStyles]);

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const defaultSelectedDomains = useSelector((state: any) => state.concierge.defaultSelectedDomains) as
        string[] | null | undefined;

    const { data } = useQuery<PromptsItem[]>({
        queryKey: ['conciergePrompts'],
        queryFn: () => conciergeAPI.getPrompts(),
        enabled: suggestedPrompts === undefined,
    });

    const conciergePrompts = useMemo(() => {
        if (defaultSelectedDomains === null) return [];
        return defaultSelectedDomains
            ? currentInitialDomainsPrompts
            : suggestedPrompts !== undefined
              ? suggestedPrompts
              : data
                ? data.map((item) => item.prompt)
                : [];
    }, [defaultSelectedDomains, currentInitialDomainsPrompts, suggestedPrompts, data]);

    const iconProps = {
        type: welcomeIcon ? 'upload' : conciergeSettings.welcome_icon_ind,
        value: welcomeIcon
            ? welcomeIcon
            : conciergeSettings.welcome_icon_ind === 'upload'
              ? `data:image/png;base64,${conciergeSettings.welcome_icon_data}`
              : conciergeSettings.welcome_icon_name,
    };

    const welcomeIconSx: SxProps<Theme> = { ...styles.welcomeIconSvg };

    if (welcomeIconHeight !== undefined) {
        welcomeIconSx.height = welcomeIconHeight;
    }

    if (welcomeIconWidth !== undefined) {
        welcomeIconSx.width = welcomeIconWidth;
    }

    const conciergeTitle = title ? title : conciergeSettings.title > '' ? conciergeSettings.title : t('welcome.title');
    const conciergeDescription = description
        ? description
        : conciergeSettings.description > ''
          ? conciergeSettings.description
          : t('welcome.sub_title');

    useEffect(() => {
        return () => {
            dispatch(setDefaultSelectedDomains(null));
        };
    }, []);
    useEffect(() => {
        if (defaultSelectedDomains) {
            const initialPrompts: string[] = [];
            defaultSelectedDomains.forEach((domainName) => {
                const domainData = availableDomains.find((domain) => domain.name === domainName) as IDomain;
                if (domainData && domainData.suggestedPrompts && domainData.suggestedPrompts.length) {
                    initialPrompts.push(...domainData.suggestedPrompts);
                }
            });
            setCurrentInitialDomainsPrompts(initialPrompts);
        }
    }, [defaultSelectedDomains]);

    return (
        <Box sx={styles.container}>
            <Box sx={styles.bgHolder}>
                <BackgroundSvg />
            </Box>
            <Stack sx={styles.content}>
                <Stack sx={styles.intro}>
                    <Box sx={styles.introIcon}>
                        <Box
                            sx={
                                conciergeSettings.welcome_icon_ind === 'upload'
                                    ? styles.welcomeIcon
                                    : { ...styles.welcomeIcon, width: '56px', height: '56px' }
                            }
                        >
                            <IconHandler icon={iconProps} sx={welcomeIconSx} />
                        </Box>
                    </Box>
                    <Box sx={styles.introTitle}>{conciergeTitle}</Box>
                    <Box
                        sx={styles.introDesc}
                        dangerouslySetInnerHTML={{
                            __html: renderShowdown(conciergeDescription),
                        }}
                    />
                </Stack>

                {conciergePrompts && conciergePrompts.length > 0 && (
                    <Box sx={styles.examples}>
                        <Box sx={styles.examplesTitle}>{t('welcome.examples_title')}</Box>
                        <Stack sx={styles.examplesList} spacing={0.5}>
                            {conciergePrompts.map((prompt, index) => {
                                return (
                                    <Stack
                                        key={index}
                                        direction={'row'}
                                        sx={styles.examplesItem}
                                        onClick={() => {
                                            setActiveChat(Date.now());
                                            setActiveChatRequest(prompt);
                                        }}
                                    >
                                        <Box flexGrow={1}>{prompt}</Box>
                                        <Box sx={styles.examplesItemIcon}>
                                            <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
